import { SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import {
  CanvasResponse,
  DecommissionWorkflowResponse,
  DeflectionConfig,
  FlowDiagramNode,
  FlowDiagramResponse,
  FlowDiagramVersion,
  Intent,
  PreviewTokenReponse,
  SlackInstallationResponse,
  SolveApiKeyResponse,
} from 'src/types/workflowBuilderAPITypes';

export const workflowBuilderCanvasApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    deleteIntent: builder.mutation<
      DecommissionWorkflowResponse,
      { intentDefinitionId: string }
    >({
      invalidatesTags: [
        'Actions',
        'ActionUsages',
        'Intents',
        'HandoffConfigurations',
        'AiInstructions',
      ],
      query: ({ intentDefinitionId }) => ({
        method: 'DELETE',
        url: `${SOLVE_V2_BASE_URL}/workflow-builder/intent/${intentDefinitionId}`,
      }),
    }),
    getIntent: builder.query<Intent, { intentDefinitionId: string }>({
      providesTags: (_result, _error, { intentDefinitionId }) => [
        { id: intentDefinitionId, type: 'Intent' },
      ],
      query: ({ intentDefinitionId }) =>
        `${SOLVE_V2_BASE_URL}/workflow-builder/intent/${intentDefinitionId}`,
    }),
    getPreviewToken: builder.query<PreviewTokenReponse, void>({
      query: () => ({
        url: SOLVE_V2_BASE_URL + '/workflow-builder/preview-token',
      }),
    }),
    getSlackInstallation: builder.query<SlackInstallationResponse, void>({
      query: () => ({ url: SOLVE_V2_BASE_URL + '/slack/installation' }),
    }),
    getSolveApiToken: builder.query<SolveApiKeyResponse, void>({
      providesTags: ['SolveApiToken'],
      query: () => ({
        url: SOLVE_V2_BASE_URL + '/workflow-builder/solve-api-token',
      }),
    }),
    getWorkflowFlowDiagram: builder.query<
      FlowDiagramResponse,
      { intentWorkflowId: string } & FlowDiagramVersion
    >({
      providesTags: (result, _error, { intentWorkflowId }) => [
        { id: intentWorkflowId, type: 'FlowDiagram' },
        ...(result?.classic_blocks ?? []).flatMap(block =>
          block.intent_workflow_id
            ? [{ id: block.intent_workflow_id, type: 'Workflow' as const }]
            : [],
        ),
      ],
      query: ({
        intentWorkflowId,
        isDraft,
        isPublished,
        version,
        versionType,
      }) => {
        const params = new URLSearchParams();
        params.set('is_draft', String(isDraft));
        if (versionType != null) {
          params.set('version_type', versionType);
        }
        if (version != null) {
          params.set('version', String(version));
        }
        if (isPublished) {
          params.set('is_published', 'true');
        }
        return `${SOLVE_V2_BASE_URL}/workflow-builder/${intentWorkflowId}/flow-diagram?${params.toString()}`;
      },
    }),
    getWorkflowsForIntent: builder.query<
      {
        api_workflow_id: string;
        has_live_interactive_email_component: boolean;
        interactive_email_workflow_id: string | null;
        playbook_workflow_id: string | null;
        slack_workflow_id: string | null;
        voice_workflow_id: string | null;
        web_call_workflow_id: string | null;
        widget_workflow_id: string;
      },
      { intentDefinitionId: string }
    >({
      query: ({ intentDefinitionId }) =>
        `${SOLVE_V2_BASE_URL}/workflow-builder/intent/${intentDefinitionId}/workflows`,
    }),
    refreshFlowDiagram: builder.mutation<
      FlowDiagramResponse,
      {
        intentWorkflowId: string;
        previousOutline: FlowDiagramNode[];
      } & FlowDiagramVersion
    >({
      // Write the response into the GET cache so subscribers see the new
      // outline without a refetch. `upsertQueryData` (rather than
      // `updateQueryData`) marks the cache entry as `fulfilled` — important
      // when the previous GET errored, since `updateQueryData` only swaps
      // `data` and would leave the error set, keeping the panel stuck on the
      // http-error view even after a successful refresh.
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            workflowBuilderCanvasApi.util.upsertQueryData(
              'getWorkflowFlowDiagram',
              {
                intentWorkflowId: args.intentWorkflowId,
                isDraft: args.isDraft,
                isPublished: args.isPublished,
                version: args.version,
                versionType: args.versionType,
              },
              data,
            ),
          );
        } catch {
          // Errors surface via the panel's error state.
        }
      },
      query: ({
        intentWorkflowId,
        isDraft,
        isPublished,
        previousOutline,
        version,
        versionType,
      }) => ({
        body: {
          is_draft: isDraft,
          is_published: isPublished ?? false,
          previous_outline: previousOutline,
          version: version ?? null,
          version_type: versionType ?? null,
        },
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/workflow-builder/${intentWorkflowId}/flow-diagram/refresh`,
      }),
    }),
    refreshSolveApiToken: builder.mutation<SolveApiKeyResponse, void>({
      invalidatesTags: ['SolveApiToken'],
      query: () => ({
        method: 'POST',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/solve-api-token',
      }),
    }),
    requestSolveApiUpgrade: builder.mutation<void, void>({
      query: () => ({
        method: 'POST',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/solve-api-upgrade-request',
      }),
    }),
    updateIsAllTags: builder.mutation<
      CanvasResponse,
      { intentWorkflowId: string; is_all_tags: boolean }
    >({
      invalidatesTags: ['Intents'],
      query: ({ intentWorkflowId, is_all_tags }) => ({
        body: { is_all_tags },
        method: 'POST',
        url:
          SOLVE_V2_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/is-all-tags`,
      }),
    }),
    updateSolveApiConfig: builder.mutation<
      DeflectionConfig,
      { config: DeflectionConfig }
    >({
      invalidatesTags: ['SolveApiConfig'],
      query: ({ config }) => ({
        body: config,
        method: 'PUT',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/solve-api-config',
      }),
    }),
    updateWorkflowTags: builder.mutation<
      CanvasResponse,
      { intentWorkflowId: string; product?: string; tags: string[] }
    >({
      invalidatesTags: ['WidgetBreakdownMetrics', 'Brands', 'Intents'],
      query: ({ intentWorkflowId, product, tags }) => ({
        body: { product, workflow_tags: tags },
        method: 'POST',
        url:
          SOLVE_V2_BASE_URL +
          `/workflow-builder/${intentWorkflowId}/workflow-tags`,
      }),
    }),
  }),
});

export const {
  useDeleteIntentMutation,
  useGetIntentQuery,
  useGetPreviewTokenQuery,
  useGetSlackInstallationQuery,
  useGetSolveApiTokenQuery,
  useGetWorkflowFlowDiagramQuery,
  useGetWorkflowsForIntentQuery,
  useLazyGetWorkflowFlowDiagramQuery,
  useLazyGetWorkflowsForIntentQuery,
  useRefreshFlowDiagramMutation,
  useRefreshSolveApiTokenMutation,
  useRequestSolveApiUpgradeMutation,
  useUpdateIsAllTagsMutation,
  useUpdateSolveApiConfigMutation,
  useUpdateWorkflowTagsMutation,
} = workflowBuilderCanvasApi;
